/**
 * JIRA Agent — PHOTON-76.
 *
 * A chat agent over LensLock's JIRA projects and tickets, modeled on the
 * Support Insights Assistant tab (thread sidebar + streaming chat + agent-step
 * cards). Single surface — no tabs.
 *
 * Streaming model:
 *   - User types a question, presses Send (or Enter).
 *   - We open an SSE stream to the jira-service. `delta` events append to the
 *     in-progress assistant bubble; each `tool_call`/`tool_result` pair renders
 *     as an expandable card. Write tools (comment / transition / update /
 *     create) get an "update" badge so mutations are visible at a glance.
 *   - `usage` and `done` close out the message; errors surface via MessageBar.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  makeStyles,
  mergeClasses,
  tokens,
  Text,
  Button,
  Textarea,
  Spinner,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Badge,
} from "@fluentui/react-components";
import {
  Send24Regular,
  Dismiss24Regular,
  Search20Regular,
  Document20Regular,
  NumberSymbol20Regular,
  FolderList20Regular,
  Info20Regular,
  People20Regular,
  Comment20Regular,
  ArrowRight20Regular,
  Edit20Regular,
  AddCircle20Regular,
  CheckmarkCircle20Filled,
  ErrorCircle20Filled,
  Add20Regular,
  Delete16Regular,
  ChatMultiple20Regular,
  TicketDiagonalRegular,
} from "@fluentui/react-icons";

import { VoiceTextInputButton } from "../../components/VoiceTextInputButton";
import {
  useJiraApiClient,
  JiraToolCall,
  JiraToolResult,
  JiraUsage,
  JiraThreadSummary,
} from "../../hooks/useJiraApiClient";

// ---------------------------------------------------------------------------
// Styles (shared look with SupportInsights)
// ---------------------------------------------------------------------------
const useStyles = makeStyles({
  planBlock: {
    marginBottom: tokens.spacingVerticalS,
    borderLeft: `2px solid ${tokens.colorNeutralStroke2}`,
    paddingLeft: tokens.spacingHorizontalM,
  },
  planSummary: {
    cursor: "pointer",
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  planBody: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  pendingText: {
    color: tokens.colorNeutralForeground3,
  },
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  assistantLayout: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    overflow: "hidden",
    minHeight: 0,
  },
  sidebar: {
    width: "260px",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: "hidden",
  },
  sidebarHeader: {
    padding: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  newChatButton: {
    width: "100%",
    justifyContent: "flex-start",
  },
  threadList: {
    flex: 1,
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    padding: tokens.spacingHorizontalXS,
    gap: "2px",
  },
  threadItem: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: "pointer",
    "&:hover": { backgroundColor: tokens.colorNeutralBackground2 },
    "&:hover .thread-delete": { opacity: 1 },
  },
  threadItemActive: {
    backgroundColor: tokens.colorNeutralBackground2Selected,
    "&:hover": { backgroundColor: tokens.colorNeutralBackground2Selected },
  },
  threadItemText: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  threadTitle: {
    fontSize: "13px",
    fontWeight: 500,
    color: tokens.colorNeutralForeground1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  threadPreview: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  threadDelete: {
    opacity: 0,
    flexShrink: 0,
    transition: "opacity 0.1s ease",
  },
  sidebarEmpty: {
    padding: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground3,
    fontSize: "12px",
    textAlign: "center",
  },
  chatPane: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  messages: {
    flex: 1,
    overflow: "auto",
    padding: tokens.spacingVerticalL,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
  },
  emptyState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground3,
    textAlign: "center",
    padding: tokens.spacingVerticalXXL,
  },
  emptySamples: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    maxWidth: "640px",
  },
  sampleButton: {
    justifyContent: "flex-start",
    textAlign: "left",
    whiteSpace: "normal",
    height: "auto",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },
  userBubble: {
    alignSelf: "flex-end",
    maxWidth: "75%",
    backgroundColor: "#004C97",
    color: "#ffffff",
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderRadius: "16px 16px 4px 16px",
    whiteSpace: "pre-wrap",
  },
  assistantBlock: {
    alignSelf: "flex-start",
    maxWidth: "92%",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  assistantBubble: {
    backgroundColor: tokens.colorNeutralBackground1,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderRadius: "16px 16px 16px 4px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: "hidden",  // contain wide pre blocks so the bubble itself stays bounded
    "& p:first-child": { marginTop: 0 },
    "& p:last-child": { marginBottom: 0 },
    "& code": {
      backgroundColor: tokens.colorNeutralBackground3,
      padding: "1px 4px",
      borderRadius: "3px",
      fontSize: "13px",
    },
    "& pre": {
      backgroundColor: tokens.colorNeutralBackground3,
      padding: tokens.spacingHorizontalS,
      borderRadius: "6px",
      fontSize: "12px",
      lineHeight: "1.4",
      overflowX: "auto",
      maxWidth: "100%",
    },
    "& pre code": {
      backgroundColor: "transparent",
      padding: 0,
      fontSize: "12px",
      whiteSpace: "pre",
    },
    "& table": {
      borderCollapse: "collapse",
      marginTop: tokens.spacingVerticalS,
      marginBottom: tokens.spacingVerticalS,
      fontSize: "13px",
    },
    "& th, & td": {
      border: `1px solid ${tokens.colorNeutralStroke2}`,
      padding: "4px 8px",
      textAlign: "left",
    },
    "& th": {
      backgroundColor: tokens.colorNeutralBackground3,
      fontWeight: 600,
    },
  },
  toolList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  toolHeader: {
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalXS,
  },
  toolCard: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "8px",
    padding: tokens.spacingHorizontalS,
    fontSize: "12px",
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    "&:hover": { backgroundColor: tokens.colorNeutralBackground2 },
  },
  toolRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  toolName: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    fontFamily: "monospace",
    fontSize: "12px",
  },
  toolSummary: {
    marginLeft: "auto",
    color: tokens.colorNeutralForeground3,
    fontSize: "11px",
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
  },
  toolDetail: {
    marginTop: tokens.spacingVerticalXS,
    padding: tokens.spacingHorizontalXS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: "4px",
    fontFamily: "monospace",
    fontSize: "11px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: tokens.colorNeutralForeground2,
    maxHeight: "240px",
    overflow: "auto",
  },
  toolError: { color: tokens.colorPaletteRedForeground1 },
  toolOk: { color: tokens.colorPaletteGreenForeground1 },
  inputRow: {
    padding: tokens.spacingVerticalL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "flex-end",
  },
  textarea: { flex: 1 },
  errorBar: {
    margin: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL} 0`,
  },
  usageRow: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    paddingLeft: tokens.spacingHorizontalM,
  },
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface ToolEvent {
  call: JiraToolCall;
  result?: JiraToolResult;
}

interface AssistantMessage {
  id: string;
  role: "user" | "assistant";
  /** Committed answer text — only prose from a round that called no tools. */
  text: string;
  /**
   * Prose the model wrote in a round that also called tools. It was written
   * BEFORE those tools ran, so it states intentions, not outcomes, and must
   * never be shown as the answer (PHOTON-107).
   */
  planText: string;
  /** Text for the round currently streaming, before we know which it is. */
  pendingText: string;
  toolEvents: ToolEvent[];
  usage?: JiraUsage;
  streaming?: boolean;
  error?: string;
}

// ---------------------------------------------------------------------------
// Chat surface
// ---------------------------------------------------------------------------
const SAMPLE_QUESTIONS = [
  "What's in progress in PHOTON right now, and who owns each ticket?",
  "Summarize PHOTON-74 including its recent comments.",
  "How many open bugs are older than 30 days? List the highest-priority ones.",
  "What shipped in the last two weeks across all projects?",
];

const TOOL_ICONS: Record<string, React.ReactNode> = {
  search_issues: <Search20Regular />,
  count_issues: <NumberSymbol20Regular />,
  get_issue: <Document20Regular />,
  list_projects: <FolderList20Regular />,
  get_project_metadata: <Info20Regular />,
  find_users: <People20Regular />,
  add_comment: <Comment20Regular />,
  transition_issue: <ArrowRight20Regular />,
  update_issue: <Edit20Regular />,
  create_issue: <AddCircle20Regular />,
};

const ToolCallCard: React.FC<{ event: ToolEvent }> = ({ event }) => {
  const styles = useStyles();
  const [expanded, setExpanded] = useState(false);
  const { call, result } = event;
  const isPending = !result;
  const isError = !!result && !result.summary.ok;
  const isWrite = !!result?.summary.write;

  // Compact one-line header per tool name
  const inputHint = useMemo(() => {
    const input = call.input as Record<string, unknown>;
    if (call.name === "search_issues" || call.name === "count_issues") {
      const jql = (input.jql as string) || "";
      return `${jql.slice(0, 90)}${jql.length > 90 ? "…" : ""}`;
    }
    if (input.issue_key) {
      const extra =
        call.name === "transition_issue"
          ? ` → ${input.transition}`
          : call.name === "add_comment"
            ? " (comment)"
            : "";
      return `${input.issue_key}${extra}`;
    }
    if (call.name === "get_project_metadata") return `${input.project_key}`;
    if (call.name === "find_users") return `"${input.query}"`;
    if (call.name === "create_issue") {
      return `${input.project_key} · ${input.issue_type}: ${String(input.summary ?? "").slice(0, 60)}`;
    }
    if (call.name === "list_projects") return "";
    return JSON.stringify(input).slice(0, 100);
  }, [call]);

  const resultHint = useMemo(() => {
    if (!result) return null;
    const s = result.summary;
    if (!s.ok) return s.error ? `error: ${s.error}` : "error";
    if (call.name === "search_issues") {
      return `${s.issue_count} issue${s.issue_count === 1 ? "" : "s"}${s.is_last_page === false ? " (more)" : ""}`;
    }
    if (call.name === "count_issues") return `≈${s.approximate_count}`;
    if (call.name === "get_issue") return `${s.issue_key}`;
    if (call.name === "list_projects") return `${s.project_count} projects`;
    if (call.name === "get_project_metadata") return `${s.issue_type_count} issue types`;
    if (call.name === "find_users") return `${s.user_count} user${s.user_count === 1 ? "" : "s"}`;
    if (call.name === "transition_issue") return `${s.issue_key} → ${s.new_status ?? "done"}`;
    if (call.name === "update_issue") return `${s.issue_key}: ${(s.updated_fields || []).join(", ")}`;
    if (call.name === "add_comment" || call.name === "create_issue") return `${s.issue_key}`;
    return "ok";
  }, [call, result]);

  return (
    <div className={styles.toolCard} onClick={() => setExpanded((e) => !e)}>
      <div className={styles.toolRow}>
        {TOOL_ICONS[call.name] || <Search20Regular />}
        <Text className={styles.toolName}>{call.name}</Text>
        {isWrite && (
          <Badge appearance="tint" color="warning" size="small">
            update
          </Badge>
        )}
        <Text size={100} style={{ color: "inherit" }}>{inputHint}</Text>
        <span className={styles.toolSummary}>
          {isPending ? (
            <Spinner size="tiny" />
          ) : isError ? (
            <span className={styles.toolError}>
              <ErrorCircle20Filled /> {resultHint}
            </span>
          ) : (
            <span className={styles.toolOk}>
              <CheckmarkCircle20Filled /> {resultHint}
            </span>
          )}
        </span>
      </div>
      {expanded && (
        <div className={styles.toolDetail}>
          input: {JSON.stringify(call.input, null, 2)}
          {result && (
            <>
              {"\n\nresult: "}
              {JSON.stringify(result.summary, null, 2)}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const AssistantBubble: React.FC<{ message: AssistantMessage }> = ({ message }) => {
  const styles = useStyles();
  const hasTools = message.toolEvents.length > 0;
  const usageHint = useMemo(() => {
    if (!message.usage) return null;
    const cached = message.usage.cache_read_input_tokens || 0;
    const created = message.usage.cache_creation_input_tokens || 0;
    const total = message.usage.input_tokens + created + cached;
    const rounds = message.usage.rounds ? ` · ${message.usage.rounds} round${message.usage.rounds === 1 ? "" : "s"}` : "";
    return `${total} in / ${message.usage.output_tokens} out${cached ? ` (cache hit: ${cached})` : ""}${rounds}`;
  }, [message.usage]);

  return (
    <div className={styles.assistantBlock}>
      {hasTools && (
        <div>
          <div className={styles.toolHeader}>agent steps</div>
          <div className={styles.toolList}>
            {message.toolEvents.map((te) => (
              <ToolCallCard key={te.call.tool_use_id} event={te} />
            ))}
          </div>
        </div>
      )}
      {message.planText && (
        <details className={styles.planBlock}>
          <summary className={styles.planSummary}>
            planned steps (written before the tools ran — not results)
          </summary>
          <div className={styles.planBody}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.planText}</ReactMarkdown>
          </div>
        </details>
      )}
      <div className={styles.assistantBubble}>
        {message.text && (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text}</ReactMarkdown>
        )}
        {message.pendingText ? (
          // Streaming, and we do not yet know whether this round will call
          // tools. Shown muted so it never reads as a completed action
          // (PHOTON-107) — it is committed or demoted at round_end.
          <div className={styles.pendingText}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.pendingText}</ReactMarkdown>
          </div>
        ) : message.streaming && !message.text ? (
          <Spinner size="tiny" label="Thinking…" />
        ) : null}
      </div>
      {message.error && (
        <MessageBar intent="warning">
          <MessageBarBody>{message.error}</MessageBarBody>
        </MessageBar>
      )}
      {usageHint && <div className={styles.usageRow}>{usageHint}</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------------
export const JiraAssistant: React.FC = () => {
  const styles = useStyles();
  const client = useJiraApiClient();
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [threads, setThreads] = useState<JiraThreadSummary[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const cancelRef = useRef<(() => void) | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // useJiraApiClient() returns a fresh object each render, so we read it
  // through a ref inside refreshThreads. That keeps refreshThreads stable
  // (empty deps) — otherwise the mount effect below would re-run every render
  // and hammer listThreads in an infinite loop.
  const clientRef = useRef(client);
  clientRef.current = client;

  const refreshThreads = useCallback(() => {
    if (!clientRef.current.isConfigured()) return;
    clientRef.current.listThreads().then(setThreads).catch(() => {/* sidebar best-effort */});
  }, []);

  useEffect(() => {
    refreshThreads();
  }, [refreshThreads]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  }, []);

  const submit = useCallback(
    async (questionRaw: string) => {
      const question = questionRaw.trim();
      if (!question || streaming) return;

      if (!client.isConfigured()) {
        setError("jiraServiceUrl is not set in config.json. Cannot reach the service.");
        return;
      }

      const userMsg: AssistantMessage = {
        id: `u_${Date.now()}`,
        role: "user",
        text: question,
        planText: "",
        pendingText: "",
        toolEvents: [],
      };
      const assistantId = `a_${Date.now()}`;
      const assistantPlaceholder: AssistantMessage = {
        id: assistantId,
        role: "assistant",
        text: "",
        planText: "",
        pendingText: "",
        toolEvents: [],
        streaming: true,
      };
      setMessages((m) => [...m, userMsg, assistantPlaceholder]);
      setInput("");
      setStreaming(true);
      setError(null);
      scrollToBottom();

      // Ensure a thread exists so this exchange is persisted and follow-ups
      // carry prior turns. Lazily created from the first question; if creation
      // fails we still answer, just statelessly for this turn.
      let tid = threadId;
      if (!tid) {
        try {
          const t = await client.createThread(question);
          tid = t.id;
          setThreadId(t.id);
          setThreads((prev) => [
            {
              id: t.id,
              title: t.title,
              message_count: 0,
              created_at: t.created_at,
              updated_at: t.updated_at,
              preview: question.slice(0, 100),
            },
            ...prev,
          ]);
        } catch {
          tid = null;
        }
      }

      const updateAssistant = (mut: (m: AssistantMessage) => AssistantMessage) => {
        setMessages((prev) =>
          prev.map((msg) => (msg.id === assistantId ? mut(msg) : msg)),
        );
      };

      cancelRef.current = client.streamAsk(
        { question, thread_id: tid ?? undefined },
        {
          onDelta: (text) => {
            // Held, not committed. A round that turns out to have called
            // tools was describing what it was about to do.
            updateAssistant((m) => ({ ...m, pendingText: m.pendingText + text }));
            scrollToBottom();
          },
          onRoundEnd: (info) => {
            updateAssistant((m) => {
              const produced = m.pendingText;
              if (!info.had_write_calls) {
                return { ...m, text: m.text + produced, pendingText: "" };
              }
              return {
                ...m,
                planText: m.planText + (m.planText && produced ? "\n\n" : "") + produced,
                pendingText: "",
              };
            });
            scrollToBottom();
          },
          onToolCall: (call) => {
            updateAssistant((m) => ({
              ...m,
              toolEvents: [...m.toolEvents, { call }],
            }));
            scrollToBottom();
          },
          onToolResult: (result) => {
            updateAssistant((m) => ({
              ...m,
              toolEvents: m.toolEvents.map((te) =>
                te.call.tool_use_id === result.tool_use_id
                  ? { ...te, result }
                  : te,
              ),
            }));
            scrollToBottom();
          },
          onUsage: (usage) => {
            updateAssistant((m) => ({ ...m, usage }));
          },
          onError: (code, message) => {
            // A mid-round failure means no round_end arrives, so text held for
            // classification would otherwise sit muted forever. Commit it —
            // the user should see what was produced — and let the error say
            // the turn did not finish.
            updateAssistant((m) => ({
              ...m,
              text: m.text + m.pendingText,
              pendingText: "",
              error: `${code}: ${message}`,
            }));
            setError(`${code}: ${message}`);
          },
          onDone: () => {
            updateAssistant((m) => ({ ...m, streaming: false }));
            setStreaming(false);
            cancelRef.current = null;
            scrollToBottom();
            // Sync sidebar so the new thread's LLM title + counts appear.
            refreshThreads();
          },
        },
      );
    },
    [client, streaming, scrollToBottom, threadId, refreshThreads],
  );

  const cancelStream = useCallback(() => {
    if (cancelRef.current) {
      cancelRef.current();
      cancelRef.current = null;
    }
    setStreaming(false);
  }, []);

  const newChat = useCallback(() => {
    if (streaming) return;
    setThreadId(null);
    setMessages([]);
    setError(null);
    setInput("");
  }, [streaming]);

  const selectThread = useCallback(
    async (id: string) => {
      if (streaming || id === threadId) return;
      setLoadingThread(true);
      setError(null);
      try {
        const t = await client.getThread(id);
        setThreadId(t.id);
        // Resumed turns are text only — no tool-step cards.
        setMessages(
          t.messages.map((m, i) => ({
            id: `${m.role}_${i}_${m.timestamp}`,
            role: m.role,
            text: m.content,
            planText: "",
        pendingText: "",
        toolEvents: [],
            streaming: false,
          })),
        );
        scrollToBottom();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load conversation");
      } finally {
        setLoadingThread(false);
      }
    },
    [client, streaming, threadId, scrollToBottom],
  );

  const removeThread = useCallback(
    async (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (streaming) return;
      try {
        await client.deleteThread(id);
      } catch {
        /* best-effort; a later refresh reconciles */
      }
      setThreads((prev) => prev.filter((t) => t.id !== id));
      if (id === threadId) {
        setThreadId(null);
        setMessages([]);
      }
    },
    [client, streaming, threadId],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        submit(input);
      }
    },
    [input, submit],
  );

  const hasMessages = messages.length > 0;

  return (
    <div className={styles.container}>
      <div className={styles.assistantLayout}>
        <div className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <Button
              className={styles.newChatButton}
              appearance="subtle"
              icon={<Add20Regular />}
              onClick={newChat}
              disabled={streaming}
            >
              New chat
            </Button>
          </div>
          {threads.length === 0 ? (
            <div className={styles.sidebarEmpty}>No conversations yet</div>
          ) : (
            <div className={styles.threadList}>
              {threads.map((t) => (
                <div
                  key={t.id}
                  className={mergeClasses(
                    styles.threadItem,
                    t.id === threadId && styles.threadItemActive,
                  )}
                  onClick={() => selectThread(t.id)}
                  title={t.title}
                >
                  <ChatMultiple20Regular />
                  <div className={styles.threadItemText}>
                    <span className={styles.threadTitle}>{t.title}</span>
                    {t.preview && (
                      <span className={styles.threadPreview}>{t.preview}</span>
                    )}
                  </div>
                  <span className={mergeClasses("thread-delete", styles.threadDelete)}>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<Delete16Regular />}
                      aria-label="Delete conversation"
                      onClick={(e) => removeThread(t.id, e)}
                      disabled={streaming}
                    />
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className={styles.chatPane}>
          {error && (
            <div className={styles.errorBar}>
              <MessageBar intent="error">
                <MessageBarBody>
                  <MessageBarTitle>Couldn't reach the JIRA Agent</MessageBarTitle>
                  {error}
                </MessageBarBody>
              </MessageBar>
            </div>
          )}

          <div ref={scrollRef} className={styles.messages}>
            {loadingThread && (
              <div className={styles.emptyState}>
                <Spinner size="medium" label="Loading conversation…" />
              </div>
            )}
            {!loadingThread && !hasMessages && (
              <div className={styles.emptyState}>
                <TicketDiagonalRegular style={{ width: 48, height: 48 }} />
                <Text size={500}>Ask anything about LensLock's JIRA projects.</Text>
                <Text size={300}>
                  Queries run live against JIRA. The agent can also make safe
                  updates for you — comment, transition, edit fields, assign,
                  and create tickets — and every change it makes is attributed
                  to you and shown in the steps above its answer.
                </Text>
                <div className={styles.emptySamples}>
                  {SAMPLE_QUESTIONS.map((q) => (
                    <Button
                      key={q}
                      className={styles.sampleButton}
                      appearance="subtle"
                      onClick={() => submit(q)}
                      disabled={streaming}
                    >
                      {q}
                    </Button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) =>
              m.role === "user" ? (
                <div key={m.id} className={styles.userBubble}>
                  {m.text}
                </div>
              ) : (
                <AssistantBubble key={m.id} message={m} />
              ),
            )}
          </div>

          <div className={styles.inputRow}>
            <div className={styles.textarea}>
              <Textarea
                value={input}
                onChange={(_, data) => setInput(data.value)}
                onKeyDown={onKeyDown}
                placeholder="Ask about JIRA tickets, or tell me what to update…"
                resize="vertical"
                disabled={streaming}
                rows={2}
                style={{ width: "100%" }}
              />
            </div>
            <VoiceTextInputButton
              value={input}
              onChange={setInput}
              onDictationComplete={(text) => submit(text)}
              disabled={streaming}
            />
            {streaming ? (
              <Button
                icon={<Dismiss24Regular />}
                onClick={cancelStream}
                appearance="secondary"
              >
                Stop
              </Button>
            ) : (
              <Button
                icon={<Send24Regular />}
                appearance="primary"
                onClick={() => submit(input)}
                disabled={!input.trim()}
              >
                Send
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default JiraAssistant;
